import { useCallback, useEffect, useMemo, useState } from 'react';
import DashboardLayout from '@/components/feature/DashboardLayout';
import CampoPeriodo from '@/components/base/CampoPeriodo';
import { useAuth } from '@/hooks/useAuth';
import { useCatalogo, type CampoExtra, type TreinamentoCatalogo } from '@/lib/catalogo';
import { LIMITE_TEXTO, LIMITE_TEXTO_LONGO } from '@/lib/mascaras';
import { periodoTexto, type PeriodoParcial } from '@/lib/periodo';
import {
  idDoNome,
  listarCategorias,
  listarItens,
  removerCategoria,
  removerItem,
  salvarCategoria,
  salvarItem,
  type CategoriaDeInfraestrutura,
  type ItemDeCatalogo,
} from '@/lib/infraestruturaCatalogoRepo';

// Catálogo de infraestrutura — configuração da plataforma (secoes 4.4 e 4.5).
//
// O admin mantém aqui o que existe no mundo: quais itens um condomínio pode ter,
// em que categoria, de quanto em quanto tempo cada um pede manutenção, que
// treinamento a operação exige e quais atributos fora do padrão o item tem.
//
// O cliente **instancia**: escolhe do catálogo e preenche valores reais. Ele não
// edita esta estrutura — é o que faz "quantos elevadores a carteira tem" ter
// resposta, em vez de virar uma contagem de textos digitados por prédio.

const CORES = ['azul', 'verde', 'amarelo', 'vermelho', 'roxo', 'ciano', 'neutro'] as const;

const TIPOS_DE_CAMPO: { valor: CampoExtra['tipo']; rotulo: string }[] = [
  { valor: 'texto', rotulo: 'Texto' },
  { valor: 'textarea', rotulo: 'Texto longo' },
  { valor: 'numero', rotulo: 'Número' },
  { valor: 'data', rotulo: 'Data' },
  { valor: 'booleano', rotulo: 'Sim / Não' },
  { valor: 'select', rotulo: 'Escolha única' },
  { valor: 'multiselect', rotulo: 'Escolha múltipla' },
];

const CLASSE =
  'w-full px-3 py-2.5 rounded-md border border-background-300/60 bg-background-50 text-sm text-foreground-900';

const CLASSE_COR: Record<string, string> = {
  azul: 'bg-blue-100 text-blue-900 border-blue-200',
  verde: 'bg-emerald-100 text-emerald-900 border-emerald-200',
  amarelo: 'bg-amber-100 text-amber-900 border-amber-200',
  vermelho: 'bg-red-100 text-red-900 border-red-200',
  roxo: 'bg-violet-100 text-violet-900 border-violet-200',
  ciano: 'bg-cyan-100 text-cyan-900 border-cyan-200',
  neutro: 'bg-background-100 text-foreground-700 border-background-300',
};

const itemVazio = (categoriaId: string): ItemDeCatalogo => ({
  id: '',
  nome: '',
  descricao: '',
  categoriaId,
  periodicidade: { valor: null, unidade: null },
  camposExtras: [],
  ativo: true,
  treinamentos: [],
});

export default function AdminInfraestruturaPage() {
  const { isSuperuser } = useAuth();
  const { itens: treinamentos } = useCatalogo<TreinamentoCatalogo>('treinamentos');

  const [categorias, setCategorias] = useState<CategoriaDeInfraestrutura[]>([]);
  const [itens, setItens] = useState<ItemDeCatalogo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  const [aba, setAba] = useState<'itens' | 'categorias'>('itens');
  const [filtroCategoria, setFiltroCategoria] = useState<string>('todas');
  const [busca, setBusca] = useState('');

  const [itemEmEdicao, setItemEmEdicao] = useState<ItemDeCatalogo | null>(null);
  const [categoriaEmEdicao, setCategoriaEmEdicao] = useState<CategoriaDeInfraestrutura | null>(
    null,
  );

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [cats, its] = await Promise.all([listarCategorias(), listarItens()]);
      setCategorias(cats);
      setItens(its);
      setErro(null);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao carregar o catálogo.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (isSuperuser) void carregar();
  }, [isSuperuser, carregar]);

  const nomeDaCategoria = useMemo(() => {
    const mapa: Record<string, CategoriaDeInfraestrutura> = {};
    for (const c of categorias) mapa[c.id] = c;
    return mapa;
  }, [categorias]);

  const itensFiltrados = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return itens.filter(
      (i) =>
        (filtroCategoria === 'todas' || i.categoriaId === filtroCategoria) &&
        (!termo || i.nome.toLowerCase().includes(termo)),
    );
  }, [itens, filtroCategoria, busca]);

  const gravarItem = async () => {
    if (!itemEmEdicao) return;
    if (!itemEmEdicao.nome.trim()) {
      setErro('Informe o nome do item.');
      return;
    }
    if (!itemEmEdicao.categoriaId) {
      setErro('Escolha a categoria.');
      return;
    }

    setSalvando(true);
    setErro(null);
    try {
      await salvarItem({
        ...itemEmEdicao,
        id: itemEmEdicao.id || idDoNome('infra', itemEmEdicao.nome),
      });
      setItemEmEdicao(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao gravar o item.');
    } finally {
      setSalvando(false);
    }
  };

  const gravarCategoria = async () => {
    if (!categoriaEmEdicao) return;
    if (!categoriaEmEdicao.nome.trim()) {
      setErro('Informe o nome da categoria.');
      return;
    }

    setSalvando(true);
    setErro(null);
    try {
      await salvarCategoria({
        ...categoriaEmEdicao,
        id: categoriaEmEdicao.id || idDoNome('cat', categoriaEmEdicao.nome).replace('cat-', ''),
      });
      setCategoriaEmEdicao(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao gravar a categoria.');
    } finally {
      setSalvando(false);
    }
  };

  const desativarItem = async (item: ItemDeCatalogo) => {
    if (
      !window.confirm(
        `Desativar "${item.nome}"? Ele some das escolhas novas; o que já foi cadastrado continua como está.`,
      )
    )
      return;
    try {
      await removerItem(item.id);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao desativar o item.');
    }
  };

  const desativarCategoria = async (categoria: CategoriaDeInfraestrutura) => {
    const usada = itens.filter((i) => i.categoriaId === categoria.id).length;
    if (
      !window.confirm(
        usada > 0
          ? `"${categoria.nome}" é usada por ${usada} item(ns). Desativar mesmo assim?`
          : `Desativar a categoria "${categoria.nome}"?`,
      )
    )
      return;
    try {
      await removerCategoria(categoria.id);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Falha ao desativar a categoria.');
    }
  };

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta área é reservada para o superusuário administrador da plataforma.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <header className="mb-6">
          <h1 className="text-xl font-bold text-foreground-900">Infraestrutura</h1>
          <p className="text-sm text-foreground-500 mt-1">
            Catálogo global de itens e categorias. O cliente escolhe daqui e preenche os valores do
            prédio dele — não edita esta estrutura.
          </p>
        </header>

        {erro && (
          <div role="alert" className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-3">
            <p className="text-sm text-red-700">{erro}</p>
          </div>
        )}

        <div className="flex items-center gap-1 mb-4 border-b border-background-200">
          {(['itens', 'categorias'] as const).map((chave) => (
            <button
              key={chave}
              type="button"
              onClick={() => setAba(chave)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                aba === chave
                  ? 'border-primary-500 text-primary-700'
                  : 'border-transparent text-foreground-500 hover:text-foreground-800'
              }`}
            >
              {chave === 'itens' ? `Itens (${itens.length})` : `Categorias (${categorias.length})`}
            </button>
          ))}
        </div>

        {carregando ? (
          <p className="text-sm text-foreground-500">Carregando o catálogo...</p>
        ) : aba === 'itens' ? (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <input
                type="search"
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar item..."
                className={`${CLASSE} max-w-xs`}
              />
              <select
                value={filtroCategoria}
                onChange={(e) => setFiltroCategoria(e.target.value)}
                aria-label="Filtrar por categoria"
                className={`${CLASSE} max-w-xs`}
              >
                <option value="todas">Todas as categorias</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nome}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => setItemEmEdicao(itemVazio(categorias[0]?.id ?? ''))}
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium"
              >
                <i className="ri-add-line"></i>
                Novo item
              </button>
            </div>

            {itensFiltrados.length === 0 ? (
              <p className="text-sm text-foreground-500">Nenhum item encontrado.</p>
            ) : (
              <ul className="space-y-2">
                {itensFiltrados.map((item) => {
                  const categoria = nomeDaCategoria[item.categoriaId];
                  return (
                    <li
                      key={item.id}
                      className="flex items-start justify-between gap-3 flex-wrap rounded-lg border border-background-200/70 bg-background-50 px-4 py-3"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-foreground-900">
                            {item.nome}
                          </span>
                          {categoria && (
                            <span
                              className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${CLASSE_COR[categoria.cor] ?? CLASSE_COR.neutro}`}
                            >
                              {categoria.nome}
                            </span>
                          )}
                          {!item.ativo && (
                            <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border bg-background-100 text-foreground-500 border-background-300">
                              Inativo
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-foreground-500 mt-0.5">
                          Manutenção: {periodoTexto(item.periodicidade)}
                          {item.camposExtras.length > 0 &&
                            ` · ${item.camposExtras.length} campo(s) extra(s)`}
                          {item.treinamentos.length > 0 &&
                            ` · ${item.treinamentos.length} treinamento(s)`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => setItemEmEdicao(item)}
                          className="p-1.5 rounded-md text-foreground-600 hover:bg-background-100"
                        >
                          <i className="ri-edit-line"></i>
                          <span className="sr-only">Editar {item.nome}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => void desativarItem(item)}
                          className="p-1.5 rounded-md text-red-600 hover:bg-red-50"
                        >
                          <i className="ri-delete-bin-line"></i>
                          <span className="sr-only">Desativar {item.nome}</span>
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        ) : (
          <>
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={() =>
                  setCategoriaEmEdicao({
                    id: '',
                    nome: '',
                    icone: '',
                    cor: 'neutro',
                    ordem: (categorias.at(-1)?.ordem ?? 0) + 10,
                    ativo: true,
                  })
                }
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium"
              >
                <i className="ri-add-line"></i>
                Nova categoria
              </button>
            </div>

            <ul className="space-y-2">
              {categorias.map((c) => {
                const usada = itens.filter((i) => i.categoriaId === c.id).length;
                return (
                  <li
                    key={c.id}
                    className="flex items-center justify-between gap-3 flex-wrap rounded-lg border border-background-200/70 bg-background-50 px-4 py-3"
                  >
                    <div className="flex items-center gap-2">
                      {c.icone && <i className={`${c.icone} text-foreground-600`}></i>}
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${CLASSE_COR[c.cor] ?? CLASSE_COR.neutro}`}
                      >
                        {c.nome}
                      </span>
                      <span className="text-xs text-foreground-500">{usada} item(ns)</span>
                      {!c.ativo && <span className="text-xs text-foreground-400">· inativa</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setCategoriaEmEdicao(c)}
                        className="p-1.5 rounded-md text-foreground-600 hover:bg-background-100"
                      >
                        <i className="ri-edit-line"></i>
                        <span className="sr-only">Editar {c.nome}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => void desativarCategoria(c)}
                        className="p-1.5 rounded-md text-red-600 hover:bg-red-50"
                      >
                        <i className="ri-delete-bin-line"></i>
                        <span className="sr-only">Desativar {c.nome}</span>
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {itemEmEdicao && (
        <FormularioDeItem
          item={itemEmEdicao}
          categorias={categorias}
          treinamentos={treinamentos}
          salvando={salvando}
          onChange={setItemEmEdicao}
          onSalvar={() => void gravarItem()}
          onFechar={() => {
            setItemEmEdicao(null);
            setErro(null);
          }}
        />
      )}

      {categoriaEmEdicao && (
        <FormularioDeCategoria
          categoria={categoriaEmEdicao}
          salvando={salvando}
          onChange={setCategoriaEmEdicao}
          onSalvar={() => void gravarCategoria()}
          onFechar={() => {
            setCategoriaEmEdicao(null);
            setErro(null);
          }}
        />
      )}
    </DashboardLayout>
  );
}

function Modal({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground-900/40 p-4">
      <div
        role="dialog"
        aria-label={titulo}
        aria-modal="true"
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-background-50 shadow-xl"
      >
        <div className="px-5 py-4 border-b border-background-200">
          <h2 className="text-base font-semibold text-foreground-900">{titulo}</h2>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

function FormularioDeItem({
  item,
  categorias,
  treinamentos,
  salvando,
  onChange,
  onSalvar,
  onFechar,
}: {
  item: ItemDeCatalogo;
  categorias: CategoriaDeInfraestrutura[];
  treinamentos: TreinamentoCatalogo[];
  salvando: boolean;
  onChange: (item: ItemDeCatalogo) => void;
  onSalvar: () => void;
  onFechar: () => void;
}) {
  const alterarCampo = (indice: number, mudanca: Partial<CampoExtra>) => {
    const campos = item.camposExtras.map((c, i) => (i === indice ? { ...c, ...mudanca } : c));
    onChange({ ...item, camposExtras: campos });
  };

  return (
    <Modal titulo={item.id ? `Editar ${item.nome}` : 'Novo item de infraestrutura'}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Nome<span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              type="text"
              value={item.nome}
              maxLength={LIMITE_TEXTO}
              onChange={(e) => onChange({ ...item, nome: e.target.value })}
              className={CLASSE}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Categoria<span className="text-red-500 ml-0.5">*</span>
            </label>
            <select
              value={item.categoriaId}
              onChange={(e) => onChange({ ...item, categoriaId: e.target.value })}
              className={CLASSE}
            >
              <option value="">Selecione...</option>
              {categorias.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground-700 mb-1.5">Descrição</label>
          <textarea
            value={item.descricao}
            maxLength={LIMITE_TEXTO_LONGO}
            rows={2}
            onChange={(e) => onChange({ ...item, descricao: e.target.value })}
            className={CLASSE}
          />
        </div>

        <CampoPeriodo
          rotulo="Periodicidade de manutenção"
          valor={item.periodicidade}
          onChange={(p: PeriodoParcial) => onChange({ ...item, periodicidade: p })}
          ajuda="De quanto em quanto tempo o item pede revisão."
          textoVazio="o item não tem manutenção recorrente — playground e salão de festas, por exemplo."
        />

        {/* Treinamentos exigidos (N:N da secao 4.5) */}
        <fieldset className="border border-background-300/60 rounded-md p-4">
          <legend className="px-1.5 text-sm font-medium text-foreground-700">
            Treinamentos que o item exige
          </legend>
          <p className="text-xs text-foreground-500 mb-2">
            Quem opera este equipamento precisa destes cursos. O vínculo é do catálogo: vale em
            qualquer condomínio onde o item exista.
          </p>
          <div className="flex flex-wrap gap-2">
            {treinamentos.length === 0 ? (
              <p className="text-xs text-foreground-500">Nenhum treinamento no catálogo.</p>
            ) : (
              treinamentos.map((t) => {
                const marcado = item.treinamentos.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() =>
                      onChange({
                        ...item,
                        treinamentos: marcado
                          ? item.treinamentos.filter((id) => id !== t.id)
                          : [...item.treinamentos, t.id],
                      })
                    }
                    aria-pressed={marcado}
                    className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                      marcado
                        ? 'bg-primary-500 text-background-50 border-primary-500'
                        : 'bg-background-50 text-foreground-700 border-background-300'
                    }`}
                  >
                    {t.nome}
                  </button>
                );
              })
            )}
          </div>
        </fieldset>

        {/* Campos extras: a definição, não a resposta */}
        <fieldset className="border border-background-300/60 rounded-md p-4">
          <legend className="px-1.5 text-sm font-medium text-foreground-700">
            Campos fora do padrão
          </legend>
          <p className="text-xs text-foreground-500 mb-3">
            Atributos que só este item tem — capacidade da piscina, potência do gerador. Aqui se
            define a <strong>pergunta</strong>; a resposta é preenchida pelo cliente em cada
            instância.
          </p>

          {item.camposExtras.length > 0 && (
            <ul className="space-y-2 mb-3">
              {item.camposExtras.map((campo, indice) => (
                <li key={indice} className="flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-[11px] text-foreground-500 mb-1">Rótulo</label>
                    <input
                      type="text"
                      value={campo.rotulo}
                      maxLength={LIMITE_TEXTO}
                      onChange={(e) => {
                        const rotulo = e.target.value;
                        alterarCampo(indice, {
                          rotulo,
                          // A chave acompanha o rótulo enquanto o campo é novo.
                          // Depois de gravado ela é a identidade da resposta, e
                          // mudá-la deixaria os valores já preenchidos órfãos.
                          chave: item.id ? campo.chave : idDoNome('c', rotulo).replace('c-', ''),
                        });
                      }}
                      className={CLASSE}
                    />
                  </div>
                  <div className="w-40">
                    <label className="block text-[11px] text-foreground-500 mb-1">Tipo</label>
                    <select
                      value={campo.tipo}
                      onChange={(e) =>
                        alterarCampo(indice, { tipo: e.target.value as CampoExtra['tipo'] })
                      }
                      className={CLASSE}
                    >
                      {TIPOS_DE_CAMPO.map((t) => (
                        <option key={t.valor} value={t.valor}>
                          {t.rotulo}
                        </option>
                      ))}
                    </select>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs pb-2.5 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={!!campo.obrigatorio}
                      onChange={(e) => alterarCampo(indice, { obrigatorio: e.target.checked })}
                      className="w-4 h-4 accent-primary-500"
                    />
                    Obrigatório
                  </label>
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        ...item,
                        camposExtras: item.camposExtras.filter((_, i) => i !== indice),
                      })
                    }
                    className="p-2 rounded-md text-red-600 hover:bg-red-50"
                  >
                    <i className="ri-delete-bin-line"></i>
                    <span className="sr-only">Remover campo {campo.rotulo}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            onClick={() =>
              onChange({
                ...item,
                camposExtras: [
                  ...item.camposExtras,
                  { chave: `campo-${item.camposExtras.length + 1}`, rotulo: '', tipo: 'texto' },
                ],
              })
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-xs font-medium text-foreground-700"
          >
            <i className="ri-add-line"></i>
            Acrescentar campo
          </button>
        </fieldset>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={item.ativo}
            onChange={(e) => onChange({ ...item, ativo: e.target.checked })}
            className="w-4 h-4 accent-primary-500"
          />
          <span className="text-foreground-700">Disponível para os clientes escolherem</span>
        </label>

        <div className="flex items-center gap-2 pt-2 border-t border-background-200">
          <button
            type="button"
            onClick={onSalvar}
            disabled={salvando}
            className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium disabled:opacity-60"
          >
            {salvando ? 'Gravando...' : 'Gravar item'}
          </button>
          <button
            type="button"
            onClick={onFechar}
            className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700"
          >
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
}

function FormularioDeCategoria({
  categoria,
  salvando,
  onChange,
  onSalvar,
  onFechar,
}: {
  categoria: CategoriaDeInfraestrutura;
  salvando: boolean;
  onChange: (c: CategoriaDeInfraestrutura) => void;
  onSalvar: () => void;
  onFechar: () => void;
}) {
  return (
    <Modal titulo={categoria.id ? `Editar ${categoria.nome}` : 'Nova categoria'}>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Nome<span className="text-red-500 ml-0.5">*</span>
            </label>
            <input
              type="text"
              value={categoria.nome}
              maxLength={LIMITE_TEXTO}
              onChange={(e) => onChange({ ...categoria, nome: e.target.value })}
              className={CLASSE}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">
              Ícone (Remix Icon)
            </label>
            <input
              type="text"
              value={categoria.icone ?? ''}
              onChange={(e) => onChange({ ...categoria, icone: e.target.value })}
              placeholder="ri-community-line"
              className={CLASSE}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Cor</label>
            <select
              value={categoria.cor}
              onChange={(e) => onChange({ ...categoria, cor: e.target.value })}
              className={CLASSE}
            >
              {CORES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground-700 mb-1.5">Ordem</label>
            <input
              type="text"
              inputMode="numeric"
              value={categoria.ordem}
              onChange={(e) =>
                onChange({ ...categoria, ordem: Number(e.target.value.replace(/\D/g, '')) || 0 })
              }
              className={CLASSE}
            />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={categoria.ativo}
            onChange={(e) => onChange({ ...categoria, ativo: e.target.checked })}
            className="w-4 h-4 accent-primary-500"
          />
          <span className="text-foreground-700">Ativa</span>
        </label>

        <div className="flex items-center gap-2 pt-2 border-t border-background-200">
          <button
            type="button"
            onClick={onSalvar}
            disabled={salvando}
            className="px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium disabled:opacity-60"
          >
            {salvando ? 'Gravando...' : 'Gravar categoria'}
          </button>
          <button
            type="button"
            onClick={onFechar}
            className="px-4 py-2 rounded-md border border-background-300/60 text-sm font-medium text-foreground-700"
          >
            Cancelar
          </button>
        </div>
      </div>
    </Modal>
  );
}
